import { useCallback, useEffect, useRef, useState } from "react";
import { MutationFunction } from "@apollo/client";
import { GenerateEnforcementPhotoUploadUrlData, GenerateEnforcementPhotoUploadUrlVars } from "common/graphql";
import { REQUEST_TIMEOUT_MS } from "@app.automotus.io/components/hooks";
import { convertHeicToJpeg } from "./convertHeicToJpeg";

export const ALLOWED_EVIDENCE_PHOTO_TYPES = ["image/jpeg", "image/png", "image/heic", "image/heif"];
// HEIC/HEIF isn't a browser-registered MIME type on many iOS versions, so a HEIC file picked via
// the Upload button (as opposed to the Capture button, which always produces a real JPEG/PNG
// from the camera) often comes through with an empty file.type -- since HEIC is the iPhone
// default and explicitly required here, checking file.type alone would reject an officer's
// existing HEIC photo from their library with a spurious "Invalid file type" error. Used as a
// fallback whenever file.type is empty (both for accepting the file at all, see
// isAllowedType/EVIDENCE_PHOTO_ACCEPT below, and for the Content-Type actually uploaded, see
// resolveContentType), never to override a type the browser actually reported.
export const ALLOWED_EVIDENCE_PHOTO_EXTENSIONS = [".jpg", ".jpeg", ".png", ".heic", ".heif"];
// isAllowedType (below) accepts an empty file.type for any of these extensions, not just
// HEIC/HEIF -- some Android file pickers and cloud-storage-backed file providers report an empty
// file.type for JPEG/PNG too, not only HEIC -- so every allowed extension needs an entry here.
const EXTENSION_TO_MIME_TYPE: Record<string, string> = {
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
  ".heic": "image/heic",
  ".heif": "image/heif",
};
export const MAX_EVIDENCE_PHOTO_SIZE_MB = 10;
const MAX_EVIDENCE_PHOTO_SIZE_BYTES = MAX_EVIDENCE_PHOTO_SIZE_MB * 1024 * 1024;

export interface EvidencePhotoValue {
  file: File;
  previewUrl: string;
}

export type GeneratePhotoUploadUrl = MutationFunction<
  GenerateEnforcementPhotoUploadUrlData,
  GenerateEnforcementPhotoUploadUrlVars
>;

interface UseEvidencePhotoUploadResult {
  handleFileSelected: (file: File) => Promise<void>;
  handleRemove: () => void;
  error: string | null;
}

/**
 * Thrown when the S3 PUT itself fails (bad status, refused/reset connection, or timeout), with
 * `message` already written to be shown directly to the officer -- distinct from a plain Error so
 * GenerateCitation's submit-error handling can recognize it and show that message instead of
 * falling back to a generic one.
 */
export class EvidencePhotoUploadError extends Error {}

// file.type is authoritative whenever the browser actually reported one. It's only empty for a
// file picked via the Upload button (as opposed to Capture, which always produces a real
// JPEG/PNG from the camera) whose source didn't report a MIME type -- known to happen for HEIC on
// iOS, but also for JPEG/PNG on some Android pickers and cloud-storage-backed file providers, so
// every extension in ALLOWED_EVIDENCE_PHOTO_EXTENSIONS needs a mapping in EXTENSION_TO_MIME_TYPE.
// Falls back to file.type (empty string) only if the extension isn't recognized either --
// handleFileSelected's own validation is what actually guarantees a recognized file reaches here,
// so that final fallback is unreachable in practice, not a silent swallow of a real case.
function resolveContentType(file: File): string {
  if (file.type) {
    return file.type;
  }
  const extension = ALLOWED_EVIDENCE_PHOTO_EXTENSIONS.find((ext) => file.name.toLowerCase().endsWith(ext));
  return (extension && EXTENSION_TO_MIME_TYPE[extension]) || file.type;
}

// isAllowedType only checks file.type/extension, which describe how the file is labeled, not
// what its bytes actually are -- a genuinely broken or mislabeled file (a corrupt download, an
// honest extension mistake) would otherwise upload unchallenged as citation evidence. This is a
// client-side data-quality guard, not an integrity barrier: it catches accidental mismatches, but
// anyone crafting their own request can bypass it entirely, so it's not a defense against
// deliberately falsified evidence -- that has to be enforced server-side on the presigned-URL
// path if it ever needs to be. Attempting a real decode (the same <img> onload/onerror check
// convertHeicToJpeg's own loadImage uses) is what actually confirms the bytes are a real image
// before they're accepted. Not needed for HEIC/HEIF -- convertHeicToJpeg already decodes the
// original file itself, and canvas.toBlob's output is a real JPEG by construction.
function verifyImageDecodes(file: File): Promise<void> {
  const objectUrl = URL.createObjectURL(file);
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve();
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Failed to decode image"));
    };
    image.src = objectUrl;
  });
}

async function putFile(presignedUrl: string, file: File, contentType: string, signal?: AbortSignal): Promise<void> {
  // The signal can already be aborted by the time this runs -- e.g. the sibling upload's presign
  // request failed and its catch called abort() while this upload was still awaiting its own
  // presign request, before ever reaching putFile. An AbortSignal only fires its "abort" event once, at the
  // moment abort() is called, so addEventListener below would never see an event that already
  // happened. Check first and reject the same way an abort during the fetch itself does, without
  // ever making the request.
  if (signal?.aborted) {
    throw new Error("Upload PUT cancelled");
  }

  // fetch takes a single signal, but this call has two independent abort sources (the caller's
  // signal, and this function's own timeout below) -- this controller is what's actually passed
  // to fetch, with the caller's signal forwarded onto it.
  const controller = new AbortController();
  // Both evidence photos upload concurrently (see GenerateCitation's onSubmit); if one fails, the
  // caller aborts this signal so the other's PUT doesn't keep running to completion after the
  // citation submission has already failed -- without this, a still-in-flight upload can finish
  // and land in S3 with no citation ever created for it, even after the officer has already seen
  // the error and cleared the form.
  const onExternalAbort = () => controller.abort();
  signal?.addEventListener("abort", onExternalAbort);
  // A stalled mobile connection never fires a fetch rejection on its own -- it just hangs.
  // Without this, that hang propagates all the way up through Promise.all in GenerateCitation's
  // onSubmit, leaving Formik stuck in isSubmitting forever with both Issue Citation and Clear
  // Form disabled and no way for the officer to recover short of reloading the page (which loses
  // the form). REQUEST_TIMEOUT_MS is shared with useApiClient's own fetch calls -- see its doc
  // comment.
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(presignedUrl, {
      method: "PUT",
      headers: { "Content-Type": contentType },
      body: file,
      signal: controller.signal,
    });
    if (!response.ok) {
      // response.status is logged by GenerateCitation's console.error, not shown to the officer
      // -- there's nothing they can do with it beyond retrying.
      throw new EvidencePhotoUploadError("Your photo failed to upload. Try again.");
    }
  } catch (err) {
    if (err instanceof EvidencePhotoUploadError) {
      throw err;
    }
    if (err instanceof DOMException && err.name === "AbortError") {
      // Both abort sources land here as the same AbortError -- signal.aborted (only ever set by
      // the caller, never by this function's own timeout) is what distinguishes a deliberate
      // cancellation (not a failure -- same "Upload PUT cancelled" Error as the up-front check
      // above) from a real timeout (a failure, shown to the officer).
      if (signal?.aborted) {
        throw new Error("Upload PUT cancelled");
      }
      throw new EvidencePhotoUploadError("Your photo upload timed out. Check your connection and try again.");
    }
    // A refused/reset connection rejects fetch with a TypeError -- same underlying condition as
    // the network-error branch in GenerateCitation's error handling for this flow's Hasura
    // requests, so it gets the same message.
    throw new EvidencePhotoUploadError("No connection. Check your network and try again.");
  } finally {
    clearTimeout(timeoutId);
    signal?.removeEventListener("abort", onExternalAbort);
  }
}

/**
 * Calls the generate_enforcement_photo_upload_url mutation, bounded by REQUEST_TIMEOUT_MS -- the
 * same stalled-mobile-connection hang putFile's own timeout guards against applies here too, and
 * Apollo has no request timeout of its own (the action's 15s timeout in actions.yaml only bounds
 * Hasura's call to the API, not the browser's call to Hasura).
 *
 * Races the mutation against the timeout rather than relying on aborting the fetch alone:
 * AuthorizedApolloProvider's RetryLink retries a failed request (up to 5 attempts, with backoff),
 * so an aborted fetch would only surface as an error after those retries had also failed. The
 * abort is still sent so the stalled request itself gets cancelled instead of left hanging --
 * HttpLink uses a signal passed in context.fetchOptions in place of its own.
 */
async function requestPhotoUploadUrl(
  generatePhotoUploadUrl: GeneratePhotoUploadUrl,
  variables: GenerateEnforcementPhotoUploadUrlVars,
): Promise<NonNullable<GenerateEnforcementPhotoUploadUrlData["generateEnforcementPhotoUploadUrl"]>> {
  const controller = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(() => {
      controller.abort();
      // Same message as putFile's own timeout -- from the officer's point of view both are just
      // "the photo upload stalled", and there's nothing different for them to do about either.
      reject(new EvidencePhotoUploadError("Your photo upload timed out. Check your connection and try again."));
    }, REQUEST_TIMEOUT_MS);
  });

  try {
    const { data } = await Promise.race([
      generatePhotoUploadUrl({ variables, context: { fetchOptions: { signal: controller.signal } } }),
      timeout,
    ]);
    const uploadUrl = data?.generateEnforcementPhotoUploadUrl;
    // Apollo already throws an ApolloError for a failed action (the response_transform always sets
    // a message on a non-2xx), so this only guards an empty result -- never expected in practice.
    if (!uploadUrl) {
      throw new Error("generate_enforcement_photo_upload_url returned no upload URL");
    }
    return uploadUrl;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Requests a presigned S3 PUT URL through the generate_enforcement_photo_upload_url Hasura Action
 * and PUTs the file directly to S3, resolving with the resulting image id. Called at
 * citation-submission time (see GenerateCitation's onSubmit),
 * deliberately not at photo-selection time -- an officer who picks a photo and then edits or
 * abandons the form before submitting must never leave a stray, citation-less upload in S3.
 *
 * A HEIC/HEIF file is already transcoded to JPEG by the time it reaches here -- see
 * convertHeicToJpeg and its call site in useEvidencePhotoUpload's handleFileSelected below --
 * so this function itself never needs to know HEIC was ever involved.
 *
 * `signal` lets the caller cancel the S3 PUT once it's in flight -- used to abort this upload if
 * its sibling (the other evidence photo, uploaded concurrently) fails first.
 */
export async function uploadEvidencePhoto(
  generatePhotoUploadUrl: GeneratePhotoUploadUrl,
  file: File,
  signal?: AbortSignal,
): Promise<string> {
  const contentType = resolveContentType(file);
  const uploadUrl = await requestPhotoUploadUrl(generatePhotoUploadUrl, { contentType, fileName: file.name });
  await putFile(uploadUrl.presignedUrl, file, contentType, signal);
  return uploadUrl.id;
}

/**
 * Evidence photo selection: validates the file and builds a local object-URL preview. Does not
 * touch the network -- the actual upload happens later, only once the officer submits the whole
 * citation (see uploadEvidencePhoto above).
 */
export function useEvidencePhotoUpload(
  onChange: (value: EvidencePhotoValue | null) => void,
): UseEvidencePhotoUploadResult {
  const [error, setError] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  // Bumped by every call to handleFileSelected or handleRemove, and captured as `selectionId`
  // at the start of each handleFileSelected call. HEIC conversion is async (see convertHeicToJpeg
  // below), so an officer can select a different photo, or remove the current one, while an
  // earlier HEIC conversion is still in flight. Comparing the captured id against the current
  // value once that conversion settles is what tells a call whether it's still the officer's
  // latest action or has been superseded -- a superseded call must not touch previewUrlRef,
  // error, or onChange, or it would silently revoke/replace whatever the officer has done since.
  const selectionIdRef = useRef(0);
  // A selection surviving until unmount is a distinct case selectionIdRef alone doesn't cover --
  // nothing bumps that counter on unmount, so a still-pending handleFileSelected call would
  // otherwise resolve after the fact, create a fresh object URL nothing will ever revoke (the
  // cleanup effect below already ran, and won't run again), and call onChange on a component
  // that's gone -- or worse, on a *new* instance that remounted in its place (e.g. Clear Form's
  // key-based remount), silently overwriting whatever it started with.
  const isMountedRef = useRef(true);

  const handleFileSelected = useCallback(
    async (file: File) => {
      const selectionId = ++selectionIdRef.current;
      // True once this specific call has been superseded by a newer one, a removal, or an
      // unmount -- checked after every await, since any of those can happen while one is pending.
      const isStale = () => !isMountedRef.current || selectionId !== selectionIdRef.current;

      const isAllowedType =
        ALLOWED_EVIDENCE_PHOTO_TYPES.includes(file.type) ||
        (file.type === "" && ALLOWED_EVIDENCE_PHOTO_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext)));
      if (!isAllowedType) {
        setError("Invalid file type. Accepted: JPEG, PNG, HEIC, HEIF");
        onChange(null);
        return;
      }
      if (file.size > MAX_EVIDENCE_PHOTO_SIZE_BYTES) {
        setError(`File too large. Maximum size is ${MAX_EVIDENCE_PHOTO_SIZE_MB}MB`);
        onChange(null);
        return;
      }

      // Converted here, at selection time, rather than deferred like the actual S3 PUT (see
      // uploadEvidencePhoto) -- an officer whose device can't decode HEIC (e.g. a genuine HEIC
      // file opened in a non-WebKit browser) should find out immediately, not after filling out
      // the rest of the form. Unlike the PUT, this is a local canvas operation with no network
      // cost, so doing it for a photo the officer might still abandon isn't worth deferring.
      let fileToStore = file;
      const resolvedType = resolveContentType(file);
      if (resolvedType === "image/heic" || resolvedType === "image/heif") {
        try {
          fileToStore = await convertHeicToJpeg(file);
        } catch (err) {
          if (!isStale()) {
            setError("This photo couldn't be processed. Try a JPG or PNG instead.");
            onChange(null);
          }
          return;
        }
      } else {
        try {
          await verifyImageDecodes(file);
        } catch (err) {
          if (!isStale()) {
            setError("This photo couldn't be processed. Try a different photo.");
            onChange(null);
          }
          return;
        }
      }

      if (isStale()) {
        return;
      }

      setError(null);
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
      const previewUrl = URL.createObjectURL(fileToStore);
      previewUrlRef.current = previewUrl;
      onChange({ file: fileToStore, previewUrl });
    },
    [onChange],
  );

  const handleRemove = useCallback(() => {
    selectionIdRef.current += 1;
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setError(null);
    onChange(null);
  }, [onChange]);

  // Revokes whatever preview URL is still outstanding on unmount (reading the ref, rather than
  // closing over previewUrl state, means this always sees the latest URL, not a stale one from
  // whenever the effect was set up), and flips isMountedRef so a still-pending handleFileSelected
  // call notices and bails out instead of resolving after the fact -- see isMountedRef's own
  // comment for why that's necessary on top of this.
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  return { handleFileSelected, handleRemove, error };
}

export default useEvidencePhotoUpload;
